import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import { getAllProveedores } from "../../../api/proveedorApi";

export default function ModalItemAlquiler({ show, onHide, onSave, itemData }) {
  const [formData, setFormData] = useState({
    coditem: "",
    item: "",
    proveedor: "",
    idproveedor: "",
    estado: "DISPONIBLE",
    obs: "",
    fechadev: "",
    esComprado: "NO",
    esAlquilado: "SI",
  });

  const [proveedores, setProveedores] = useState([]);
  const [loadingProveedores, setLoadingProveedores] = useState(false);

  const estadoOptions = [
    { value: "DISPONIBLE", label: "DISPONIBLE (item disponible para alquilar)" },
    { value: "EN MANTENIMIENTO", label: "EN MANTENIMIENTO" },
    { value: "FUERA DE SERVICIO", label: "FUERA DE SERVICIO" },
  ];

  // ⬇️ Cargar proveedores una sola vez
  useEffect(() => {
    const fetchProveedores = async () => {
      try {
        setLoadingProveedores(true);
        const data = await getAllProveedores();
        const formatted = data.map((prov) => ({
          value: Number(prov.idProveedores),
          label:
            prov.proveedor ||
            prov.razonsocial ||
            `Proveedor #${prov.idProveedores}`,
        }));
        setProveedores(formatted);
      } catch (err) {
        console.error("Error al cargar proveedores:", err);
      } finally {
        setLoadingProveedores(false);
      }
    };

    fetchProveedores();
  }, []);

  // ⬇️ Corregido: limpiar o cargar datos SOLO cuando se abre el modal
  useEffect(() => {
    if (!show) return;

    if (itemData) {
      // 🔒 Modo edición
      setFormData({
        coditem: itemData.coditem || "",
        item: itemData.item || "",
        proveedor: itemData.proveedor || "",
        idproveedor: itemData.idProveedores
          ? Number(itemData.idProveedores)
          : "",
        estado: (itemData.estado || "DISPONIBLE").toUpperCase(),
        obs: itemData.obs || "",
        fechadev: itemData.fechadev || "",
        esComprado: itemData.esComprado || "NO",
        esAlquilado: itemData.esAlquilado || "SI",
      });
    } else {
      // ➕ Modo nuevo (siempre limpio)
      setFormData({
        coditem: "",
        item: "",
        proveedor: "",
        idproveedor: "",
        estado: "DISPONIBLE",
        obs: "",
        fechadev: "",
        esComprado: "NO",
        esAlquilado: "SI",
      });
    }
  }, [show, itemData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (!formData.item || !formData.coditem) {
      alert("⚠️ Los campos Código e Ítem son obligatorios.");
      return;
    }

    const dataToSave = {
      ...formData,
      estado: formData.estado.toUpperCase(),
    };

    onSave(dataToSave);
    onHide();
  };

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "8px",
      borderColor: state.isFocused ? "#0d6efd" : "#ced4da",
      boxShadow: state.isFocused
        ? "0 0 0 0.2rem rgba(13,110,253,0.25)"
        : "none",
      minHeight: "38px",
      "&:hover": { borderColor: "#0d6efd" },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  const isAlquilado = formData.estado?.toUpperCase() === "ALQUILADO";

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {itemData ? "Editar Ítem de Alquiler" : "Agregar Ítem de Alquiler"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Código (editable solo en nuevo) */}
          <Form.Group className="mb-3">
            <Form.Label>Código del Ítem</Form.Label>
            <Form.Control
              type="text"
              name="coditem"
              value={formData.coditem}
              onChange={handleChange}
              placeholder="Ej: ALQ-001"
              disabled={!!itemData} // 🔥 Bloqueado solo en edición
            />
            <Form.Text className="text-muted">
              {itemData
                ? "El código del ítem no puede modificarse en edición."
                : "Ingrese un código único para el nuevo ítem."}
            </Form.Text>
          </Form.Group>

          {/* Nombre */}
          <Form.Group className="mb-3">
            <Form.Label>Nombre del Ítem</Form.Label>
            <Form.Control
              type="text"
              name="item"
              value={formData.item}
              onChange={handleChange}
              placeholder="Ej: SILLA DE RUEDAS"
            />
          </Form.Group>

          {/* Proveedor */}
          <Form.Group className="mb-3">
            <Form.Label>Proveedor</Form.Label>
            {loadingProveedores ? (
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                Cargando proveedores...
              </div>
            ) : (
              <Select
                options={proveedores}
                value={
                  proveedores.find(
                    (opt) => opt.value === Number(formData.idproveedor)
                  ) || null
                }
                onChange={(opt) =>
                  setFormData((prev) => ({
                    ...prev,
                    idproveedor: opt ? opt.value : "",
                    proveedor: opt ? opt.label : "",
                  }))
                }
                styles={selectStyles}
                placeholder="SELECCIONAR PROVEEDOR"
                isClearable
                isSearchable
              />
            )}
          </Form.Group>

          {/* Estado */}
          <Form.Group className="mb-3">
            <Form.Label>Estado</Form.Label>
            <Select
              options={estadoOptions}
              value={{
                value: formData.estado?.toUpperCase(),
                label: formData.estado?.toUpperCase(),
              }}
              onChange={(opt) =>
                setFormData((prev) => ({
                  ...prev,
                  estado: opt.value.toUpperCase(),
                }))
              }
              styles={selectStyles}
              placeholder="SELECCIONAR ESTADO"
              isDisabled={isAlquilado}
            />
            {isAlquilado && (
              <Form.Text className="text-muted">
                No se puede cambiar el estado mientras el ítem esté alquilado.
              </Form.Text>
            )}
          </Form.Group>

          {/* Observaciones */}
          <Form.Group className="mb-3">
            <Form.Label>Observaciones</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="obs"
              value={formData.obs}
              onChange={handleChange}
              placeholder="Notas o detalles adicionales"
            />
          </Form.Group>

          {/* Tipo de Ítem */}
          <Form.Group className="mb-3">
            <Form.Label>Tipo de Ítem</Form.Label>
            <div className="d-flex gap-4 mt-2">
              <Form.Check
                type="radio"
                id="radioComprado"
                label="Comprado"
                name="tipoItem"
                checked={formData.esComprado === "SI"}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    esComprado: "SI",
                    esAlquilado: "NO",
                    fechadev: "",
                  }))
                }
              />
              <Form.Check
                type="radio"
                id="radioAlquilado"
                label="Alquilado"
                name="tipoItem"
                checked={formData.esAlquilado === "SI"}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    esAlquilado: "SI",
                    esComprado: "NO",
                  }))
                }
              />
            </div>
          </Form.Group>

          {/* Fecha de devolución (solo alquilados) */}
          {formData.esAlquilado === "SI" && formData.esComprado !== "SI" && (
            <Form.Group className="mb-3">
              <Form.Label>Fecha de Devolución</Form.Label>
              <Form.Control
                type="date"
                name="fechadev"
                value={formData.fechadev}
                onChange={handleChange}
              />
            </Form.Group>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="danger" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {itemData ? "Guardar Cambios" : "Agregar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
